import get from 'lodash/get'
import {all, call, put, select, take, takeEvery, takeLatest} from 'redux-saga/effects'

import history from '../../history'

import {
  ADD_PRODUCT_TO_LOOK,
  ADD_PRODUCTS_TO_EXISTING_LOOK,
  ADD_PRODUCTS_TO_LOOKBUILDER,
  ADD_PRODUCTS_TO_NEW_LOOK,
  ADD_RECOMMENDATION_MODAL_PRODUCT,
  ADD_RECOMMENDED_PRODUCTS_TO_LOOK,
  addProductsToExistingLook,
  addToLook,
  addToLookFailure,
  addToLookIdle,
  addToLookSuccess,
  addToNewLook,
  handleAddProductsToLookbuilder,
} from '../../actions/addToLook'
import {CREATE_EVENT_LOOK_SUCCESS, createEventLook, LOGIN_SUCCESS} from '../../actions/api'
import {addLook, addLookItem} from '../../actions/looks'
import {closeSlidingModal} from '../../actions/modal'
import {updateIsOwnerWearingLookSuccess} from '../../actions/people'
import {
  CLOSE_MODAL,
  closeModal,
  openErrorModal,
  openRecommendationModal,
} from '../../actions/userInteractions'
import {isBadResponse} from '../../api/statusCodes'
import * as trackAtl from '../../track/addToLook'

import {
  ATL_ORIGIN_HTO,
  ATL_ORIGIN_PDP,
  TRANSACTION_TYPE_BUY,
  TRANSACTION_TYPE_HTO,
  TRANSACTION_TYPE_RENT,
  TRANSACTION_TYPE_RENT_ADD,
} from '../../const'

import * as lookbuilderActions from '../../actions/lookbuilder'
import * as productDetailPageActions from '../../actions/productDetailPage'
import * as userActions from '../../actions/user'
import * as optimizely from '../../analytics/optimizely'
import * as api from '../../api/api'
import * as onboardingActions from '../../components/Onboarding/redux/actions'
import * as cross from '../../store/cross'
import * as looks from '../../store/looks'
import * as productSelectors from '../../store/product'
import * as pdp from '../../store/productDetailPage'
import * as track from '../../track/track'
import * as URLS from '../../urls'

import {handleFlowCompletion} from './helpers'

export * from './createEventLook'
export * from './hto'
export * from './origins'

// Handle Add Product Instrumentation
// ----------------------------------------------------------------------------

export function* handleAddProductInstrumentation(
  categorySlug,
  origin,
  productName,
  setType,
  transactionType,
) {
  const addedProduct = yield select(pdp.addedProductObject)
  yield all([
    call(trackAtl.addProduct, {
      addedProduct,
      categorySlug,
      origin,
      productName,
      setType,
      transactionType,
    }),
  ])

  const optimizelyEventNames = {
    [TRANSACTION_TYPE_BUY]: 'Added Buy New Product',
    [TRANSACTION_TYPE_HTO]: 'Added HTO Product',
    [TRANSACTION_TYPE_RENT]: 'Added Rental Product',
    [TRANSACTION_TYPE_RENT_ADD]: 'Added Rental Product to Existing Order',
  }

  yield call(optimizely.fireOptimizelyEvent, optimizelyEventNames[transactionType])
}

// Handle Add to Or Create Look
// ----------------------------------------------------------------------------

/**
 * Helper function - creates a properly formatted payload object that the
 * API accepts. Used for adding to or creating a look with a product or outfit.
 * The server only accepts 'rent' or 'buy' transaction types, so we must
 * normalize any non-accepted transaction types here.
 *
 * @param {String} lookName The name of the look if creating a new look
 * @param {String} setType One of either 'group' or 'single'
 * @param {String} slug The individual product slug
 * @param {String} transactionType Accepts all transaction types
 * @return {Object} An object normalized for the server
 */
export const createAddToOrCreateLookPayload = (lookName, setType, slug, transactionType) => {
  const transactionTypeMap = {
    [TRANSACTION_TYPE_BUY]: TRANSACTION_TYPE_BUY,
    [TRANSACTION_TYPE_HTO]: TRANSACTION_TYPE_RENT,
    [TRANSACTION_TYPE_RENT]: TRANSACTION_TYPE_RENT,
  }

  return {
    type: setType === 'group' ? 'outfits' : 'items',
    payload: {
      event_look_name: lookName,
      [setType === 'group' ? 'outfit_slug' : 'product_slug']: slug,
      transaction_type: transactionTypeMap[transactionType],
    },
  }
}

export function* handleAddToOrCreateLook(lookId, lookName, product) {
  const {categorySlug, name, origin, setType, slug, transactionType} = product
  const {type, payload} = yield call(
    createAddToOrCreateLookPayload,
    lookName,
    setType,
    slug,
    transactionType,
  )
  const response = yield call(api.addToOrCreateLook, lookId, type, payload)
  if (isBadResponse(response.status)) {
    throw new Error(`Could not add to or create look: ${response.status}`)
  }
  yield call(handleAddProductInstrumentation, categorySlug, origin, name, setType, transactionType)
  yield put(addLookItem(get(response, 'json', {})))
}

// Handle Recommendation Modal
// ----------------------------------------------------------------------------

export function* handleRecommendationModal() {
  yield put(closeSlidingModal())
  yield put(openRecommendationModal())
  yield call(trackAtl.viewedRecommendationModal)

  const {payload, type} = yield take([ADD_RECOMMENDATION_MODAL_PRODUCT, CLOSE_MODAL])
  if (type === ADD_RECOMMENDATION_MODAL_PRODUCT) {
    yield put(closeModal())
  }
  if (type === CLOSE_MODAL) {
    return []
  }
  return payload.products
}

// Add Product To Existing Look
// ----------------------------------------------------------------------------

export function* addProductsToExistingLookWorker({payload: {lookId, products}}) {
  const isSuitOrTux = yield select(pdp.productIsSuitTuxedo)
  const isAddToLookHto = products[0] && products[0].origin === ATL_ORIGIN_HTO
  const isAddToLookPdp = products[0] && products[0].origin === ATL_ORIGIN_PDP
  let recommendedProducts = []

  if (isSuitOrTux && isAddToLookPdp && !isAddToLookHto) {
    recommendedProducts = yield call(handleRecommendationModal)
  }

  const productsArray = [...products, ...recommendedProducts]
  try {
    yield all(productsArray.map((product) => call(handleAddToOrCreateLook, lookId, null, product)))
    yield put(addToLookSuccess())
  } catch (error) {
    yield put(addToLookFailure(error))
    yield put(openErrorModal())
  }
}

export function* addProductsToExistingLookSaga() {
  yield takeEvery(ADD_PRODUCTS_TO_EXISTING_LOOK, addProductsToExistingLookWorker)
}

// Add Product to Look
// ----------------------------------------------------------------------------

export function* addProductToLookWorker({payload: {lookId, origin}}) {
  const buyExperiment = origin.includes('Buy Experiment')
  const product = !buyExperiment
    ? yield select(cross.addToLookProductsArray)
    : yield select(cross.addToLookBuyExperimentProductsArray)

  const thunk = lookId
    ? yield call(addToLook, lookId, product)
    : yield call(addToNewLook, product, origin)
  yield put(thunk)
  yield call(handleFlowCompletion)
}

export function* addProductToLookSaga() {
  yield takeLatest(ADD_PRODUCT_TO_LOOK, addProductToLookWorker)
}

// Complete The Look
// ----------------------------------------------------------------------------

export function* addRecommendedProductsToLookWorker({payload: {lookId, origin}}) {
  const products = yield select(productSelectors.addToLookRecommendedItemsArray)
  const thunk = lookId
    ? yield call(addToLook, lookId, products)
    : yield call(addToNewLook, products, origin)
  yield call(track.pdpAddedCompleteLook)
  yield put(thunk)
  yield call(handleFlowCompletion)
}

export function* addRecommendedProductsToLookSaga() {
  yield takeLatest(ADD_RECOMMENDED_PRODUCTS_TO_LOOK, addRecommendedProductsToLookWorker)
}

/**
 * Saga which handles adding a single product or multiple products to a new look. This saga is explicitly called
 * after the user has selected a look name from the look name panel.
 *
 * @param lookName {String} Name of the look selected by user to add product(s) to
 * @param origin {String} Determines the original source of where user initiated flow (for analytics purposes)
 * @param products {Object|Object[]} A single product or array or product objects
 * @generator
 */
export function* addProductsToNewLookWorker({payload: {lookName, origin, products}}) {
  yield put(createEventLook(lookName, origin))
  const {payload: response} = yield take(CREATE_EVENT_LOOK_SUCCESS)

  if (!(yield select(looks.hasNonHtoLooks))) {
    // this will be customer's first non-hto look
    yield put(updateIsOwnerWearingLookSuccess({lookId: response.id, isOwnerWearingLook: true}))
  }

  yield put(addLook(response))

  const payload = {
    lookId: response.id,
    products,
  }

  yield put(addProductsToExistingLook(payload))
}

export function* addProductsToNewLookSaga() {
  yield takeEvery([ADD_PRODUCTS_TO_NEW_LOOK], addProductsToNewLookWorker)
}

/**
 * We want to reset the addToLook status if a user bails on registration or login
 * when attempting to add product(s) to a look. However, we don't want to reset
 * it blindly on the `closeOnboarding` action, as that fires whether a user bails
 * or is successful. Here, we listen for all 3 possible outcomes after a user
 * initiates onboarding and only reset the add to look status if the next action
 * is close.
 */
export function* addToLookOnboardingWorker() {
  const {type} = yield take([
    onboardingActions.CLOSE_ONBOARDING,
    userActions.REGISTRATION_FINISHED,
    LOGIN_SUCCESS,
  ])
  if (type === onboardingActions.CLOSE_ONBOARDING) {
    yield put(addToLookIdle())
  }
}

export function* addToLookOnboardingSaga() {
  yield takeLatest(onboardingActions.OPEN_ONBOARDING, addToLookOnboardingWorker)
}

/**
 * This is kind of a cop-out because the party available modal does not expose
 * much for us to work with. Resetting to idle whenever the modal closes at
 * least shields us from maintaining a "pending" status if they bail. If they
 * do end up adding to look, we get "pending" => "idle" -> "success". Since we're
 * not using these statuses on PDP's yet, I think this is a worthy trade-off at
 * the current time.
 */
export function* addToLookPartyAvailableWorker() {
  yield put(addToLookIdle())
}

export function* addToLookPartyAvailableSaga() {
  yield takeLatest(
    productDetailPageActions.CLOSE_PARTY_AVAILABLE_MODAL,
    addToLookPartyAvailableWorker,
  )
}

/**
 * Saga which handles adding a product and its accessories to lookbuilder from the PDP.
 *
 * @param {Object[]} products Array of product slugs
 */
export function* addProductsToLookbuilderWorker({payload: {products}}) {
  const addProductsToLookbuilderThunk = yield call(handleAddProductsToLookbuilder, products)
  yield put(addProductsToLookbuilderThunk)
  yield take(lookbuilderActions.QUEUE_LOOKBUILDER_PRODUCTS)
  yield call(history.push, URLS.LOOKBUILDER)
}

export function* addProductsToLookbuilderSaga() {
  yield takeLatest([ADD_PRODUCTS_TO_LOOKBUILDER], addProductsToLookbuilderWorker)
}
